import React, { FC, useEffect, useRef } from 'react';
import { FamiliesTable } from '../../components/families-table';
import { Box, Chip, Container, InputAdornment, MenuItem, Typography } from '@mui/material';
import { CoreTextField } from '../../../_core/components/_ui/core-textfield';
import { CoreButton } from '../../../_core/components/_ui/core-button';
import { useTranslation } from 'react-i18next';
import {
	useGetFamiliesListQuery,
	useCreateFamilyMutation,
	useDeleteFamilyMutation,
	useUpdateFamilyMutation,
	useCheckExistOneRosterSettingsQuery,
} from '../../../../store/api/families.api';
import {
	CreateFamilyDto,
	FamilyDto,
	LanguageEnum,
	PermissionDto,
	UpdateFamilyDto,
	UserRolesEnum,
} from '@families-link/shared';
import { generatePath, useNavigate } from 'react-router-dom';
import { DistrictRoutes } from '../../../district/constants';
import {
	useSearch,
	useSortingParams,
	usePaginatedData,
	useLanguageFilter,
	useDistrictFilter,
	useMuiModal,
} from '../../../_core/hooks';
import { AutocompleteTextField, CoreAutoComplete } from '../../../_core/components/_ui/core-autocomplete';
import { getObjectLanguage } from '../../../_core/components/languages-component';
import { useOpenModalForm } from '../../hooks/modal-forms/use-open-modal-form.hook';
import { useOpenConfirmModal } from '../../hooks/modal-forms/use-open-confirm-modal-form.hook';
import { useCurrentAccessTokenSelector } from '../../../auth';
import { useGetDistrictListQuery } from '../../../../store/api/district.api';
import { ChipCloseIcon, ConfirmationDeleteIcon } from '../../../_core/components/icons';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { FamilyImportModal } from '../../components/modal-import-form/modal-import-form.component';
import { Search } from '../../../_core/constants/icons.constants';
import { ProfileRoutes } from '../../../profile/constants';
import { CoreCheckbox } from '../../../_core/components/_ui/core-checkbox';
import { boxHeaderStyles, buttonHeaderStyles, chipDeleteIconStyles } from '../../../_core/styles';
import { CoreOverflowTypographyTooltip } from '../../../_core/components/_ui/core-overflow-tooltip';
import {
	OneRosterFamilyImportModal,
	OneRosterFamilyImportModalProps,
} from '../../components/modal-import-form/oneroster-modal-import-form.component';
import ApiIcon from '@mui/icons-material/Api';
import { FamilyColumnName } from '../../hooks/table-columns/families-table-columns.hook';
import { AllowedFamilyActions } from '../../components/families-table/families-table-actions.component';
import { OneRosterSettingsFamilyModal, OneRosterSettingsFamilyModalProps } from '../../components/modal-oneroster-settings-form/oneroster-settings-modal-form.component';
import { skipToken } from '@reduxjs/toolkit/query';

export type FamiliesListPageProps = {
	isAdminRoute: boolean;
	currentDistrictId?: string | null;
	permission?: PermissionDto;
	familyColumnsList: FamilyColumnName[];
	allowedFamilyActions: AllowedFamilyActions[];
};

export const FamiliesListPage: FC<FamiliesListPageProps> = (props) => {
	const { currentDistrictId, permission, isAdminRoute, familyColumnsList, allowedFamilyActions } = props;

	const [t] = useTranslation();
	const navigate = useNavigate();
	const { register, search } = useSearch();

	const currentToken = useCurrentAccessTokenSelector();
	const isRequester = currentToken?.payload.sur === UserRolesEnum.Requester;
	const isFirstRender = useRef(true);
	const { data: hasOneRoster } = useCheckExistOneRosterSettingsQuery(currentDistrictId ?? skipToken);

	const { sortingArray, sortingParams, handleSortingChange } = useSortingParams();
	const { register: registerLang, getValues, control, languages } = useLanguageFilter();
	const { data: districtList } = useGetDistrictListQuery();
	const {
		register: registerDistrict,
		getValues: getValuesDistrict,
		control: controlDistrict,
		districtIds,
		districts,
	} = useDistrictFilter();

	const { requestObject, meta, page, handleResetPage, itemsList, fetchMore } = usePaginatedData<FamilyDto>(
		useGetFamiliesListQuery,
		{
			search,
			...sortingParams,
			languages: languages.join(','),
			district: currentDistrictId,
			districts: districtIds.join(','),
		},
		{ refetchOnMountOrArgChange: true }
	);
	const { refetch, isFetching } = requestObject;

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;

			return;
		}
		if (page !== 1) {
			handleResetPage();
		}
	}, [sortingArray, search, languages, districts, handleResetPage]);

	const handlePagination = () => {
		handleResetPage();

		if (page === 1) {
			refetch();
		}
	};

	const [createFamily] = useCreateFamilyMutation();
	const featchActionCreate = async (data: CreateFamilyDto) => {
		await createFamily(data).unwrap();
	};
	const handleOpenModalCreate = useOpenModalForm({
		featchAction: featchActionCreate,
		handlePagination,
		isCreate: true,
		districtId: currentDistrictId,
		submitButtonName: t('families.forms.buttons.create'),
		toastText: 'Family contact was successfully created',
	});

	const [updateFamily] = useUpdateFamilyMutation();
	const featchActionEdit = async (data: UpdateFamilyDto) => {
		await updateFamily({ id: data._id.toString(), data }).unwrap();
	};
	const handleOpenModalEdit = useOpenModalForm({
		featchAction: featchActionEdit,
		handlePagination,
		isCreate: false,
		districtId: currentDistrictId,
		submitButtonName: t('families.forms.buttons.save'),
		toastText: 'Family contact was successfully updated',
	});

	const [deleteFamily] = useDeleteFamilyMutation();
	const featchActionDelete = async (data: FamilyDto) => {
		await deleteFamily({ id: data._id }).unwrap();
	};
	const handleOpenDeleteConfirmModal = useOpenConfirmModal({
		featchAction: featchActionDelete,
		handlePagination,
		title: t('families.forms.delete-title'),
		description: t('families.forms.delete-description'),
		icon: <ConfirmationDeleteIcon sx={{ fontSize: '40px', color: (theme) => theme.colors.red }} />,
		submitButtonName: t('families.forms.buttons.delete'),
		toastText: 'Family contact was successfully deleted',
		danger: true,
	});

	const handleOnView = (family: FamilyDto) => {
		if (isAdminRoute) {
			navigate(generatePath(ProfileRoutes.FamilyDetails, { familyId: family._id }));
		} else {
			navigate(generatePath(DistrictRoutes.Families.Details, { familyId: family._id }));
		}
	};

	const { openModal, closeModal } = useMuiModal(FamilyImportModal);

	const handleOpenImportModal = () => {
		openModal({
			submitAction: async (formData: FormData) => Promise.resolve(),
			closeModal,
			districtId: currentDistrictId,
		});
	};

	const { openModal: openModalOneRoster, closeModal: closeModalOneRoster } =
		useMuiModal<OneRosterFamilyImportModalProps>(OneRosterFamilyImportModal);

	const handleOpenOneRosterImport = () => {
		openModalOneRoster({ closeModal: closeModalOneRoster, districtId: currentDistrictId });
	};

	const { openModal: openModalOneRosterSettings, closeModal: closeModalOneRosterSettings } = 
		useMuiModal<OneRosterSettingsFamilyModalProps>(OneRosterSettingsFamilyModal);
	
	const handleOpenOneRosterSettings = () => {
		openModalOneRosterSettings({ closeModal: closeModalOneRosterSettings, districtId: currentDistrictId, isFirstImport: true });
	}

	return (
		<Container
			maxWidth={false}
			sx={{ display: 'flex', flexDirection: 'column', height: '100%', paddingBottom: '16px' }}
		>
			<Box sx={boxHeaderStyles}>
				<CoreTextField
					{...register('search')}
					hasEndAdornment
					placeholder={t('families.search-by-name-or-student')}
					endAdornment={
						<InputAdornment position="end">
							<Search />
						</InputAdornment>
					}
					sx={{ marginRight: 1, minWidth: '280px' }}
				/>
				<CoreAutoComplete
					multiple={true}
					{...registerLang('languages')}
					control={control}
					controlSx={{ minWidth: '220px', marginRight: 1 }}
					isFilter
					options={Object.values(LanguageEnum)}
					getOptionLabel={(option) => {
						const language = getObjectLanguage(option);

						return language ? language.name : '';
					}}
					selectOnFocus={true}
					isOptionEqualToValue={(option, value) => option === value}
					renderInput={(params) => (
						<AutocompleteTextField
							{...params}
							placeholder={!getValues('languages')?.length ? t('common.filter-lang') : ''}
							sx={{
								'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
									padding: '4.5px 4px 5.5px 5px',
									minWidth: 0,
								},
								'& .MuiInputBase-input': {
									height: '100%',
									padding: '0',
								},
								'& .MuiOutlinedInput-root': {
									'&.Mui-focused fieldset': {
										borderColor: (theme) => theme.colors.blue,
									},
								},
							}}
						/>
					)}
					renderOption={(props, option, { selected }) => {
						const language = getObjectLanguage(option);

						return (
							<MenuItem {...props} key={language?.value}>
								<CoreCheckbox checked={selected} sx={{ mr: 2, p: 0, ml: 0 }} />
								<CoreOverflowTypographyTooltip>{language?.name}</CoreOverflowTypographyTooltip>
							</MenuItem>
						);
					}}
					renderTags={(value, getTagProps) => {
						const count = value.length - 1;

						return [
							value[0] && (
								<Chip
									sx={chipDeleteIconStyles}
									{...getTagProps({ index: 0 })}
									label={getObjectLanguage(value[0])?.name}
									deleteIcon={<ChipCloseIcon />}
								/>
							),
							count > 0 && (
								<Chip
									sx={chipDeleteIconStyles}
									{...getTagProps({ index: count })}
									label={`+${count}`}
									deleteIcon={<ChipCloseIcon />}
								/>
							),
						];
					}}
				/>
				{permission?.read ? (
					<CoreAutoComplete
						multiple={true}
						{...registerDistrict('districts')}
						control={controlDistrict}
						controlSx={{ minWidth: '190px' }}
						options={districtList || []}
						isFilter
						getOptionLabel={(option) => option.name || ''}
						selectOnFocus={true}
						isOptionEqualToValue={(option, value) => option._id === value._id}
						renderInput={(params) => (
							<AutocompleteTextField
								{...params}
								placeholder={!getValuesDistrict('districts')?.length ? t('common.filter-district') : ''}
								sx={{
									'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
										padding: '4.5px 4px 5.5px 5px',
										minWidth: 0,
									},
									'& .MuiInputBase-input': {
										height: '100%',
										padding: '0',
									},
									'& .MuiOutlinedInput-root': {
										'&.Mui-focused fieldset': {
											borderColor: (theme) => theme.colors.blue,
										},
									},
								}}
							/>
						)}
						renderOption={(props, option) => (
							<MenuItem {...props} key={option._id}>
								<CoreOverflowTypographyTooltip>{option.name}</CoreOverflowTypographyTooltip>
							</MenuItem>
						)}
						renderTags={(value, getTagProps) => {
							const count = value.length - 1;

							return [
								value[0] && (
									<Chip
										sx={chipDeleteIconStyles}
										{...getTagProps({ index: 0 })}
										label={value[0]?.name}
										deleteIcon={<ChipCloseIcon />}
									/>
								),
								count > 0 && (
									<Chip
										sx={chipDeleteIconStyles}
										{...getTagProps({ index: count })}
										label={`+${count}`}
										deleteIcon={<ChipCloseIcon />}
									/>
								),
							];
						}}
					/>
				) : null}

				{!isRequester && permission?.create ? (
					<>
						<CoreButton
							tooltipTitle={t('buttons.tooltips.import-oneroster')}
							sx={{ marginLeft: 'auto', display: 'flex', gap: 1, marginRight: '8px' }}
							variant="base"
							circle
							size="large"
							onClick={hasOneRoster ? handleOpenOneRosterImport : handleOpenOneRosterSettings}
						>
							<ApiIcon color="primary" />
						</CoreButton>
						<CoreButton
							tooltipTitle={t('buttons.tooltips.import')}
							sx={{ display: 'flex', gap: 1, marginRight: '8px' }}
							variant="base"
							circle
							size="large"
							onClick={handleOpenImportModal}
						>
							<FileUploadOutlinedIcon color="primary" />
						</CoreButton>

						<CoreButton
							onClick={() => {
								handleOpenModalCreate();
							}}
							sx={buttonHeaderStyles}
						>
							{t('families.add-family')}
						</CoreButton>
					</>
				) : null}
			</Box>
			<FamiliesTable
				data={itemsList}
				loading={isFetching}
				isSkeletonLoader={page === 1 && itemsList?.length && isFetching}
				hasMore={meta?.hasNextPage}
				onDelete={handleOpenDeleteConfirmModal}
				onEdit={handleOpenModalEdit}
				onView={handleOnView}
				sorting={sortingArray}
				onSortingChange={handleSortingChange}
				fetchMore={fetchMore}
				familyColumnsList={familyColumnsList}
				allowedFamilyActions={allowedFamilyActions}
			/>
		</Container>
	);
};
